import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Input, Empty } from "../components/UI";
import { CocoaSelect } from "../components/CocoaSelect";
import { ClientSelect } from "./clinical/shared";
import { DateField } from "./engagement/DateField";
import { ListChecks, ClipboardList, FileWarning, ShieldAlert, PenLine, CalendarX, Inbox, FileSignature, RefreshCw, ExternalLink, PartyPopper, Search, FilterX } from "lucide-react";
import { toast } from "sonner";

const CAT_ICON = { tasks: ListChecks, intake_tasks: ClipboardList, plan_reviews: FileWarning, authorizations: ShieldAlert, unsigned_notes: PenLine, unsigned_consents: FileSignature, missed_appointments: CalendarX, stale_requests: Inbox };

export default function OverdueCenter() {
  useEffect(() => { document.title = "Overdue Center | COCOA EHR"; }, []);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [userF, setUserF] = useState("");
  const [clientF, setClientF] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const debounce = useRef(null);

  const params = () => ({
    search: search.trim() || undefined, user_id: userF || undefined, client_id: clientF || undefined,
    date_from: dateFrom || undefined, date_to: dateTo || undefined,
  });
  const load = () => api.get("/overdue-center", { params: params() }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.status === "active"))).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { load(); }, search ? 300 : 0);
    return () => clearTimeout(debounce.current);
  }, [search, userF, clientF, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasFilters = !!(search.trim() || userF || clientF || dateFrom || dateTo);
  const clearFilters = () => { setSearch(""); setUserF(""); setClientF(""); setDateFrom(""); setDateTo(""); };

  if (!data) return <div className="space-y-4"><div className="h-24 animate-pulse rounded-2xl bg-[#EFEBE9]" /><div className="h-64 animate-pulse rounded-2xl bg-[#EFEBE9]" /></div>;

  const cats = data.categories.filter((c) => !filter || c.key === filter);

  return (
    <div data-testid="overdue-center-page">
      <PageHeader title="Overdue Center" subtitle={`${data.total} item${data.total === 1 ? "" : "s"} overdue across the entire clinic · generated ${dayjs(data.generated_at).format("MMM D, h:mm A")}`}
        action={<Btn variant="outline" data-testid="overdue-refresh" onClick={() => { load(); toast.success("Refreshed"); }}><RefreshCw size={14} /> Refresh</Btn>} />

      <Card className="mb-5 p-4" data-testid="overdue-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#84675B]" />
            <Input data-testid="overdue-search" className="h-10 w-56 pl-9" placeholder="Search overdue items…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <CocoaSelect testId="overdue-user-filter" className="w-48" placeholder="All staff" value={userF} onChange={setUserF}
            options={users.map((u) => [u.id, `${u.first_name} ${u.last_name}`])} />
          <div className="w-56"><ClientSelect testId="overdue-client-filter" clients={clients} value={clientF} onChange={setClientF} /></div>
          <DateField testId="overdue-date-from" placeholder="From" className="w-36" value={dateFrom} disabledAfter={dateTo || undefined} onChange={setDateFrom} />
          <DateField testId="overdue-date-to" placeholder="To" className="w-36" value={dateTo} disabledBefore={dateFrom || undefined} onChange={setDateTo} />
          {hasFilters && (
            <button data-testid="overdue-clear-filters" onClick={clearFilters}
              className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-[#7B6157] hover:bg-[#F5F2EB]"><FilterX size={13} /> Clear filters</button>
          )}
        </div>
      </Card>

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="overdue-summary">
        {data.categories.map((c) => {
          const I = CAT_ICON[c.key] || ListChecks;
          const active = filter === c.key;
          return (
            <button key={c.key} data-testid={`overdue-cat-${c.key}`} onClick={() => setFilter(active ? "" : c.key)}
              className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${active ? "border-[#4E342E] bg-[#F5F2EB]" : "border-[#EFEBE0] bg-white hover:bg-[#FDFCF8]"}`}>
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${c.count > 0 ? "bg-[#FFEBEE] text-[#C62828]" : "bg-[#E8F5E9] text-[#2E7D32]"}`}><I size={16} /></span>
              <span>
                <span className="block font-heading text-lg font-bold leading-tight text-[#3E2723]">{c.count}</span>
                <span className="block text-[11px] leading-tight text-[#7B6157]">{c.label}</span>
              </span>
            </button>
          );
        })}
      </div>

      {data.total === 0 && (hasFilters ? (
        <Card className="p-8"><Empty text="No overdue items match your filters" /></Card>
      ) : (
        <Card className="p-10 text-center">
          <PartyPopper size={32} className="mx-auto mb-2 text-[#2E7D32]" />
          <div className="font-heading text-lg font-bold text-[#3E2723]">Nothing is overdue</div>
          <div className="text-sm text-[#7B6157]">The whole clinic is caught up. Great work, team.</div>
        </Card>
      ))}

      <div className="space-y-5">
        {cats.filter((c) => c.count > 0).map((c) => {
          const I = CAT_ICON[c.key] || ListChecks;
          return (
            <Card key={c.key} className="p-5" data-testid={`overdue-section-${c.key}`}>
              <div className="mb-3 flex items-center gap-2">
                <I size={15} className="text-[#C62828]" />
                <h3 className="font-heading text-sm font-bold text-[#3E2723]">{c.label}</h3>
                <span className="rounded-full bg-[#FFEBEE] px-2 py-0.5 text-[10px] font-bold text-[#C62828]">{c.count}</span>
                <button onClick={() => navigate(c.link)} className="ml-auto flex items-center gap-1 text-xs font-semibold text-[#7B6157] hover:underline">View all <ExternalLink size={11} /></button>
              </div>
              <div className="space-y-1.5">
                {c.items.map((it, i) => (
                  <button key={i} data-testid={`overdue-item-${c.key}-${i}`} onClick={() => navigate(it.link)}
                    className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-2.5 text-left transition-colors hover:bg-[#FDFCF8]">
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${it.state === "expiring" ? "bg-[#FFF8E1] text-[#876600]" : "bg-[#FFEBEE] text-[#C62828]"}`}>
                      {it.state === "expiring" ? `Expires ${dayjs(it.date).format("MMM D")}` : `${it.days}d overdue`}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-[#3E2723]">{it.title}</span>
                      {it.sub && <span className="block text-[11px] text-[#7B6157]">{it.sub}</span>}
                    </span>
                    <span className="ml-auto text-[11px] tabular-nums text-[#84675B]">{it.date ? dayjs(it.date).format("MMM D, YYYY") : ""}</span>
                  </button>
                ))}
                {c.count > c.items.length && <div className="pt-1 text-center text-[11px] text-[#84675B]">Showing {c.items.length} of {c.count}. Use "View all" for the full list.</div>}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
